'use client';

import * as SwitchPrimitive from '@radix-ui/react-switch';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { cn } from '../utils.ts';

interface SwitchProps extends ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  label?: ReactNode;
  hint?: ReactNode;
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { label, hint, id, className, ...rest },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? `switch-${reactId}`;
  return (
    <div className="flex items-start gap-3">
      <SwitchPrimitive.Root
        ref={ref}
        id={inputId}
        className={cn(
          'group relative inline-flex h-6 w-10 flex-none items-center rounded-pill border border-transparent bg-light-silver',
          'transition-colors duration-fast ease-clickt',
          'data-[state=checked]:bg-electric',
          'focus-visible:outline-none focus-visible:shadow-focus-ring',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          className,
        )}
        {...rest}
      >
        <SwitchPrimitive.Thumb
          className={cn(
            'block h-5 w-5 rounded-pill bg-white shadow-soft translate-x-0.5 transition-transform duration-fast ease-clickt',
            'data-[state=checked]:translate-x-[18px]',
          )}
        />
      </SwitchPrimitive.Root>
      {label ? (
        <label htmlFor={inputId} className="text-body-md text-midnight leading-snug cursor-pointer">
          {label}
          {hint ? <span className="block text-[13px] text-cool-gray mt-0.5">{hint}</span> : null}
        </label>
      ) : null}
    </div>
  );
});
